import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';

import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { PasswordResetService } from '../../services/password-reset.service';

@Component({
    selector: 'app-forgot-password',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        RouterLink,
        MatButtonModule,
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatProgressSpinnerModule,
    ],
    templateUrl: './forgot-password.component.html',
    styleUrl: './forgot-password.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ForgotPasswordComponent {
    private readonly fb = inject(FormBuilder);
    private readonly passwordResetService = inject(PasswordResetService);
    private readonly destroyRef = inject(DestroyRef);

    private readonly cooldownDurationMs = 5 * 60 * 1000;
    private readonly cooldownStorageKey = 'forgot-password-cooldown-until';
    private readonly cooldownEmailStorageKey = 'forgot-password-cooldown-email';
    private cooldownTimerId: ReturnType<typeof setInterval> | null = null;

    protected readonly isSubmitting = signal(false);
    protected readonly serverError = signal<string | null>(null);
    protected readonly successMessage = signal<string | null>(null);

    protected readonly cooldownSeconds = signal(0);

    protected readonly isCooldownActive = computed(
        () => this.cooldownSeconds() > 0
    );

    protected readonly cooldownText = computed(() => {
        const totalSeconds = this.cooldownSeconds();

        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;

        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    });

    protected readonly form = this.fb.nonNullable.group({
        email: ['', [Validators.required, Validators.email]],
    });

    constructor() {
        this.restoreCooldown();

        this.destroyRef.onDestroy(() => {
            this.stopCooldownTimer();
        });
    }

    protected submit(): void {
        this.serverError.set(null);
        this.successMessage.set(null);

        if (this.isSubmitting() || this.isCooldownActive()) {
            return;
        }

        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        this.isSubmitting.set(true);

        const email = this.form.controls.email.value.trim();

        this.passwordResetService.forgotPassword(email).subscribe({
            next: (response) => {
                this.isSubmitting.set(false);

                this.successMessage.set(response.message);
                this.form.controls.email.setValue(email);

                this.startCooldown(
                    Date.now() + this.cooldownDurationMs,
                    email
                );
            },

            error: () => {
                this.isSubmitting.set(false);

                this.serverError.set(
                    'Die Anfrage konnte nicht verarbeitet werden. Bitte versuchen Sie es später erneut.'
                );
            },
        });
    }

    private restoreCooldown(): void {
        const storedCooldownUntil = Number(
            localStorage.getItem(this.cooldownStorageKey)
        );
        const storedEmail = localStorage.getItem(this.cooldownEmailStorageKey)?.trim() ?? '';

        if (
            !Number.isFinite(storedCooldownUntil) ||
            storedCooldownUntil <= Date.now()
        ) {
            this.clearPersistedCooldownState();
            return;
        }

        if (storedEmail) {
            this.form.controls.email.setValue(storedEmail);
        }

        this.startCooldown(storedCooldownUntil, undefined, false);
    }

    private startCooldown(
        cooldownUntil: number,
        email?: string,
        persist = true
    ): void {
        if (persist) {
            localStorage.setItem(
                this.cooldownStorageKey,
                cooldownUntil.toString()
            );

            if (email) {
                localStorage.setItem(this.cooldownEmailStorageKey, email);
            }
        }

        this.stopCooldownTimer();

        this.updateCooldown(cooldownUntil);

        this.cooldownTimerId = setInterval(() => {
            this.updateCooldown(cooldownUntil);
        }, 1000);
    }

    private updateCooldown(cooldownUntil: number): void {
        const remainingMs = cooldownUntil - Date.now();

        if (remainingMs <= 0) {
            this.cooldownSeconds.set(0);

            this.clearPersistedCooldownState();

            this.stopCooldownTimer();
            return;
        }

        this.cooldownSeconds.set(
            Math.ceil(remainingMs / 1000)
        );
    }

    private stopCooldownTimer(): void {
        if (this.cooldownTimerId === null) {
            return;
        }

        clearInterval(this.cooldownTimerId);

        this.cooldownTimerId = null;
    }

    private clearPersistedCooldownState(): void {
        localStorage.removeItem(this.cooldownStorageKey);
        localStorage.removeItem(this.cooldownEmailStorageKey);
    }
}
